import React from "react";
import { useNavigate, Link } from "react-router-dom";
import CountUp from "react-countup";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { ShowcaseVideo } from "../components/ShowcaseVideo";
import {
  ArrowRight, ShieldCheck, Radar, Fingerprint, Server,
  Eye, BrainCircuit, Activity, Waves, ChevronRight,
  Anchor, Plane, Flame, Mountain, Building2,
  Zap, Cpu, Lock, Search, FileSearch, Scale, AlertOctagon,
  Bluetooth, MapPin, AlertCircle, Wifi,
} from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/11783119/pexels-photo-11783119.jpeg";
const ATLAS_IMG = "https://images.pexels.com/photos/25391056/pexels-photo-25391056.jpeg";
const SYGNAL_IMG = "https://images.unsplash.com/photo-1764170347100-36f930110fb7";
const ATLAS_VIDEO = "https://customer-assets.emergentagent.com/job_atlas-shield-hub/artifacts/hve5j320_Atlas_vid_3.mp4";
const SAFELINK_HERO_IMG = "https://customer-assets.emergentagent.com/job_atlas-shield-hub/artifacts/3dc6pqo1_SafeLink_-.jpeg";
const SYGNAL_ACCESSORY_IMG = "https://customer-assets.emergentagent.com/job_atlas-shield-hub/artifacts/bz8fgjhm_Sygnal%20Shield%20_1.jpeg";
const SYGNAL_LOGO_DARK_BG = "https://customer-assets.emergentagent.com/job_atlas-shield-hub/artifacts/jp3l3gtk_Sygnal%20Shiel%20Logo%20%282%29.jpeg";
const GLOBAL_IMG = "https://images.pexels.com/photos/17272862/pexels-photo-17272862.jpeg";
const INTEG_IMG = "https://images.pexels.com/photos/4508751/pexels-photo-4508751.jpeg";
const SAFELINK_IMG = "https://customer-assets.emergentagent.com/job_atlas-shield-hub/artifacts/3dc6pqo1_SafeLink_-.jpeg";
const RADAR_IMG = "https://images.pexels.com/photos/355948/pexels-photo-355948.jpeg";
const ARMOR_IMG = "https://images.pexels.com/photos/1432673/pexels-photo-1432673.jpeg";
const FORENSIC_IMG = "https://images.pexels.com/photos/2882566/pexels-photo-2882566.jpeg";

const SECTORS = [
  { id: "ports", icon: Anchor, secondary: Plane, titleKey: "sectorPortsTitle", descKey: "sectorPortsDesc" },
  { id: "oil", icon: Flame, titleKey: "sectorOilTitle", descKey: "sectorOilDesc" },
  { id: "mining", icon: Mountain, titleKey: "sectorMiningTitle", descKey: "sectorMiningDesc" },
  { id: "gov", icon: Building2, titleKey: "sectorGovTitle", descKey: "sectorGovDesc" },
];

const STATS = [
  { end: 180, suffix: "+", animate: true,  key: "stat1" }, // Sitios protegidos
  { staticVal: "24/7",     animate: false, key: "stat2" }, // Operaciones 24/7 — constante, no anima
  { end: 60,  suffix: "+", animate: true,  key: "stat3" }, // Integraciones nativas
  { end: 3,   suffix: "",  animate: true,  key: "stat4" }, // Países activos
];

const COUNTRIES = [
  { code: "CA", name: "Canada", coords: "43.65°N · 79.38°W", desc: "Toronto · Smart City Integrations & Critical Transit" },
  { code: "CL", name: "Chile", coords: "23.65°S · 70.40°W", desc: "Antofagasta · Energy & Mining Critical Infrastructure" },
  { code: "AR", name: "Argentina", coords: "32.95°S · 60.65°W", desc: "Rosario · Buenos Aires · Ports & Aviation" },
];

const Landing = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const goPortal = () => navigate(user && typeof user === "object" ? "/dashboard" : "/login");

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="landing-page">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden grid-bg">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(5,5,5,0.4) 0%, rgba(5,5,5,0.95) 60%, #050505 100%), url(${HERO_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 lg:col-span-9">
              <p className="tactical-label reveal" data-testid="hero-eyebrow">
                <span className="inline-block w-8 h-px bg-[#19A7E0] mr-3 align-middle" />
                {t("landing.eyebrow")}
              </p>
              <h1
                className="reveal reveal-1 mt-6 text-4xl sm:text-5xl lg:text-7xl font-medium tracking-tight leading-[0.95]"
                data-testid="hero-title"
              >
                {t("landing.heroTitle")}
              </h1>
              <p className="reveal reveal-2 mt-8 text-base lg:text-lg text-white/70 max-w-2xl leading-relaxed">
                {t("landing.heroSubtitle")}
              </p>
              <div className="reveal reveal-3 mt-10 flex flex-wrap gap-3">
                <button
                  onClick={goPortal}
                  data-testid="hero-cta-primary"
                  className="inline-flex items-center gap-2 px-6 py-3.5 btn-primary text-sm font-mono uppercase tracking-[0.18em] rounded-sm"
                >
                  {t("landing.ctaPrimary")} <ArrowRight className="w-4 h-4" />
                </button>
                <a
                  href="#atlas"
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center gap-2 px-6 py-3.5 btn-ghost text-sm font-mono uppercase tracking-[0.18em] rounded-sm"
                >
                  {t("landing.ctaSecondary")}
                </a>
              </div>
            </div>
          </div>

          {/* Stats strip */}
          <div className="reveal reveal-4 mt-20 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/[0.08] border border-white/[0.08]">
            {STATS.map((s, i) => (
              <div key={i} className="bg-[#050505] p-6 lg:p-8" data-testid={`hero-stat-${i}`}>
                <p className="text-3xl lg:text-4xl font-medium tracking-tight text-white tabular-nums">
                  {s.animate ? (
                    <>
                      <CountUp
                        end={s.end}
                        duration={2.5}
                        enableScrollSpy
                        scrollSpyOnce
                        data-testid={`hero-stat-counter-${i}`}
                      />
                      {s.suffix && <span className="text-[#19A7E0]">{s.suffix}</span>}
                    </>
                  ) : (
                    <span data-testid={`hero-stat-static-${i}`}>{s.staticVal}</span>
                  )}
                </p>
                <p className="mt-2 tactical-label">{t(`landing.${s.key}`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ShowcaseVideo />

      {/* PRODUCTS */}
      <section className="relative py-24 lg:py-32" data-testid="products-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <p className="tactical-label">{t("landing.productsEyebrow")}</p>
            <h2 className="mt-4 text-3xl lg:text-5xl font-medium tracking-tight text-white">
              {t("landing.productsTitle")}
            </h2>
          </div>

          <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* ATLAS */}
            <article id="atlas" className="lg:col-span-7 card-sharp overflow-hidden group" data-testid="product-atlas">
              <div className="aspect-[16/8] relative overflow-hidden bg-black">
                <video
                  src={ATLAS_VIDEO}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="absolute inset-0 w-full h-full object-cover opacity-90"
                  data-testid="atlas-bg-video"
                />
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,5,5,0) 30%, rgba(10,13,20,0.95) 100%)" }} />
                <div className="absolute top-6 left-6 tactical-label z-10">{t("nav.atlas")}</div>
                <div className="absolute bottom-6 left-6 right-6 z-10 flex items-end justify-between">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#19A7E0]">v12.4.3 · LIVE INSPECTION</p>
                </div>
              </div>
              <div className="p-8">
                <h3 className="text-2xl lg:text-3xl tracking-tight font-medium">{t("landing.atlasTitle")}</h3>
                <p className="mt-4 text-sm text-white/70 leading-relaxed">{t("landing.atlasDesc")}</p>
                <ul className="mt-6 space-y-2">
                  {[
                    { icon: Fingerprint, key: "atlasFeat1" },
                    { icon: Eye, key: "atlasFeat2" },
                    { icon: BrainCircuit, key: "atlasFeat3" },
                    { icon: Activity, key: "atlasFeat4" },
                  ].map(({ icon: Icon, key }) => (
                    <li key={key} className="flex items-center gap-3 text-sm">
                      <Icon className="w-4 h-4 text-[#19A7E0]" />
                      <span>{t(`landing.${key}`)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </article>

            {/* SYGNAL */}
            <article id="sygnal" className="lg:col-span-5 card-sharp overflow-hidden" data-testid="product-sygnal">
              <div
                className="aspect-[5/4] relative overflow-hidden bg-black"
                style={{
                  backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.92) 100%), url(${SYGNAL_IMG})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              >
                <div className="absolute top-6 left-6 tactical-label">{t("nav.sygnal")}</div>
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <img
                    src={SYGNAL_LOGO_DARK_BG}
                    alt="Sygnal Shield"
                    className="h-24 lg:h-28 w-auto opacity-95 select-none"
                    draggable={false}
                    data-testid="sygnal-product-logo"
                  />
                </div>
              </div>
              <div className="p-8">
                <h3 className="text-2xl lg:text-3xl tracking-tight font-medium">{t("landing.sygnalTitle")}</h3>
                <p className="mt-4 text-sm text-white/70 leading-relaxed">{t("landing.sygnalDesc")}</p>
                <ul className="mt-6 space-y-2">
                  {[
                    { icon: ShieldCheck, key: "sygnalFeat1" },
                    { icon: Waves, key: "sygnalFeat2" },
                    { icon: Activity, key: "sygnalFeat3" },
                  ].map(({ icon: Icon, key }) => (
                    <li key={key} className="flex items-center gap-3 text-sm">
                      <Icon className="w-4 h-4 text-[#19A7E0]" />
                      <span>{t(`landing.${key}`)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </article>

            {/* INTEGRATIONS */}
            <article id="integrations" className="lg:col-span-12 card-sharp overflow-hidden" data-testid="product-integrations">
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div
                  className="aspect-[16/9] md:aspect-auto md:min-h-[360px] relative"
                  style={{
                    backgroundImage: `linear-gradient(90deg, rgba(10,13,20,0.2) 0%, rgba(10,13,20,1) 100%), url(${INTEG_IMG})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                >
                  <div className="absolute top-6 left-6 tactical-label">{t("nav.integrations")}</div>
                </div>
                <div className="p-8 lg:p-12 flex flex-col justify-center">
                  <h3 className="text-2xl lg:text-3xl tracking-tight font-medium">{t("landing.integTitle")}</h3>
                  <p className="mt-4 text-sm text-white/70 leading-relaxed max-w-md">{t("landing.integDesc")}</p>
                  <ul className="mt-8 space-y-3">
                    {[
                      { icon: Radar, key: "integFeat1" },
                      { icon: Fingerprint, key: "integFeat2" },
                      { icon: Server, key: "integFeat3" },
                    ].map(({ icon: Icon, key }) => (
                      <li key={key} className="flex items-center gap-3 text-sm py-1.5 border-b border-white/[0.06]">
                        <Icon className="w-4 h-4 text-[#19A7E0]" />
                        <span>{t(`landing.${key}`)}</span>
                        <ChevronRight className="ml-auto w-4 h-4 text-white/30" />
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* SECTORS */}
      <section
        id="sectors"
        className="relative py-24 lg:py-32 border-t border-white/[0.08]"
        data-testid="sectors-section"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <p className="tactical-label">{t("landing.sectorsEyebrow")}</p>
            <h2 className="mt-4 text-3xl lg:text-5xl font-medium tracking-tight">
              {t("landing.sectorsTitle")}
            </h2>
            <p className="mt-5 text-base text-white/65 leading-relaxed">
              {t("landing.sectorsDesc")}
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.08] border border-white/[0.08]">
            {SECTORS.map((s, i) => {
              const Icon = s.icon;
              return (
                <article
                  key={s.id}
                  data-testid={`sector-${s.id}`}
                  className="bg-[#050505] p-8 lg:p-10 hover:bg-[#0A0D14] transition-colors group min-h-[280px] flex flex-col"
                >
                  <div className="flex items-center gap-2">
                    <Icon className="w-6 h-6 text-[#19A7E0]" />
                    {s.secondary && <s.secondary className="w-5 h-5 text-[#19A7E0]/60" />}
                  </div>
                  <p className="mt-6 text-[10px] font-mono uppercase tracking-[0.25em] text-white/40">
                    {String(i + 1).padStart(2, "0")} / 04
                  </p>
                  <h3 className="mt-3 text-xl font-medium tracking-tight">
                    {t(`landing.${s.titleKey}`)}
                  </h3>
                  <p className="mt-4 text-sm text-white/60 leading-relaxed flex-1">
                    {t(`landing.${s.descKey}`)}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* SAFELINK (UA350) */}
      <section
        id="safelink"
        className="relative py-24 lg:py-32 border-t border-white/[0.08]"
        data-testid="safelink-section"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-stretch">
            <div
              className="lg:col-span-6 card-sharp min-h-[400px] relative overflow-hidden"
              style={{
                backgroundImage: `linear-gradient(135deg, rgba(5,5,5,0.55) 0%, rgba(10,13,20,0.7) 100%), url(${SAFELINK_IMG})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <div className="absolute top-6 left-6 tactical-label">UA350 · UWB</div>
              <div className="absolute bottom-6 left-6 right-6 grid grid-cols-3 gap-px bg-white/10 border border-white/10">
                {[
                  { label: "RANGE", val: "0–100 m" },
                  { label: "PRECISION", val: "± 10 cm" },
                  { label: "RATING", val: "IP65/IP67" },
                ].map((m) => (
                  <div key={m.label} className="bg-[#050505]/90 backdrop-blur p-4">
                    <p className="font-mono text-base lg:text-lg text-[#19A7E0]">{m.val}</p>
                    <p className="mt-1 text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">
                      {m.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-6 flex flex-col">
              <p className="tactical-label">{t("landing.safelinkEyebrow")}</p>
              <h2 className="mt-4 text-3xl lg:text-4xl font-medium tracking-tight">
                {t("landing.safelinkTitle")}
              </h2>
              <p className="mt-5 text-base text-white/65 leading-relaxed">
                {t("landing.safelinkDesc")}
              </p>
              <ul className="mt-8 space-y-3">
                {[
                  { icon: MapPin, key: "safelinkFeat1" },
                  { icon: AlertCircle, key: "safelinkFeat2" },
                  { icon: Bluetooth, key: "safelinkFeat3" },
                ].map(({ icon: Icon, key }) => (
                  <li
                    key={key}
                    data-testid={`safelink-feat-${key}`}
                    className="flex items-center gap-3 text-sm py-2 border-b border-white/[0.06]"
                  >
                    <Icon className="w-4 h-4 text-[#19A7E0]" />
                    <span>{t(`landing.${key}`)}</span>
                    <span className="ml-auto text-[10px] font-mono uppercase tracking-[0.25em] text-white/30">
                      SafeLink
                    </span>
                  </li>
                ))}
              </ul>
              <Link
                to="/safelink"
                data-testid="safelink-explore-link"
                className="mt-8 inline-flex items-center gap-2 px-6 py-3 btn-primary text-sm font-mono uppercase tracking-[0.18em] rounded-sm self-start"
              >
                {t("safelink.ctaPrimary")} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* RADAR DE GRADO MILITAR */}
      <section
        id="radar"
        className="relative py-24 lg:py-32 border-t border-white/[0.08]"
        data-testid="radar-section"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <p className="tactical-label">{t("landing.radarEyebrow")}</p>
            <h2 className="mt-4 text-3xl lg:text-5xl font-medium tracking-tight">
              {t("landing.radarTitle")}
            </h2>
            <p className="mt-5 text-base text-white/65 leading-relaxed">
              {t("landing.radarDesc")}
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div
              className="lg:col-span-7 card-sharp min-h-[360px] relative overflow-hidden"
              style={{
                backgroundImage: `linear-gradient(180deg, rgba(5,5,5,0.55) 0%, rgba(5,5,5,0.92) 100%), url(${RADAR_IMG})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <div className="absolute top-6 left-6 tactical-label">PERIMETER · 360°</div>
              <div className="absolute bottom-6 left-6 right-6 flex items-end gap-6">
                <Radar className="w-12 h-12 text-[#19A7E0]" />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
                    LONG-RANGE
                  </p>
                  <p className="font-mono text-2xl text-[#19A7E0]">SR-1000 · 1 km</p>
                </div>
              </div>
            </div>
            <div className="lg:col-span-5 grid grid-cols-1 gap-3">
              {[
                { icon: Activity, key: "radarFeat1" },
                { icon: ShieldCheck, key: "radarFeat2" },
                { icon: Server, key: "radarFeat3" },
                { icon: Wifi, key: "radarFeat4" },
              ].map(({ icon: Icon, key }) => (
                <div
                  key={key}
                  data-testid={`radar-feat-${key}`}
                  className="card-sharp p-5 flex items-start gap-3"
                >
                  <Icon className="w-5 h-5 text-[#19A7E0] shrink-0" />
                  <p className="text-sm text-white/85 leading-relaxed">
                    {t(`landing.${key}`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ELECTRONIC ARMOR */}
      <section
        id="armor"
        className="relative py-24 lg:py-32 border-t border-white/[0.08]"
        data-testid="armor-section"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-stretch">
            <div className="lg:col-span-5 flex flex-col">
              <p className="tactical-label">{t("landing.armorEyebrow")}</p>
              <h2 className="mt-4 text-3xl lg:text-4xl font-medium tracking-tight">
                {t("landing.armorTitle")}
              </h2>
              <p className="mt-5 text-base text-white/65 leading-relaxed">
                {t("landing.armorDesc")}
              </p>

              <ul className="mt-8 space-y-3">
                {[
                  { icon: Lock, key: "armorPoint1" },
                  { icon: Zap, key: "armorPoint2" },
                  { icon: Cpu, key: "armorPoint3" },
                  { icon: ShieldCheck, key: "armorPoint4" },
                ].map(({ icon: Icon, key }) => (
                  <li
                    key={key}
                    data-testid={`armor-point-${key}`}
                    className="flex items-center gap-3 text-sm py-2 border-b border-white/[0.06]"
                  >
                    <Icon className="w-4 h-4 text-[#19A7E0]" />
                    <span>{t(`landing.${key}`)}</span>
                    <span className="ml-auto text-[10px] font-mono uppercase tracking-[0.25em] text-white/30">
                      Sygnal Shield
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div
              className="lg:col-span-7 card-sharp min-h-[420px] relative overflow-hidden"
              style={{
                backgroundImage: `linear-gradient(135deg, rgba(5,5,5,0.7) 0%, rgba(10,13,20,0.4) 60%, rgba(5,5,5,0.85) 100%), url(${ARMOR_IMG})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <div className="absolute inset-0 grid-bg opacity-40" />
              <div className="absolute top-6 left-6 right-6 flex items-start justify-between">
                <div className="tactical-label">EMC · TEMPEST · HEMP</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#19A7E0]">
                  Sygnal Shield · HW
                </div>
              </div>
              <div className="absolute bottom-6 left-6 right-6 grid grid-cols-3 gap-px bg-white/10 border border-white/10">
                {[
                  { label: "ATTENUATION", val: "100 dB" },
                  { label: "STANDARDS", val: "MIL-STD-188" },
                  { label: "TIER", val: "SCIF · IV" },
                ].map((m) => (
                  <div key={m.label} className="bg-[#050505]/90 backdrop-blur p-4">
                    <p className="font-mono text-base lg:text-lg text-[#19A7E0]">{m.val}</p>
                    <p className="mt-1 text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">
                      {m.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FORENSIC INFORMATICS */}
      <section
        id="forensic"
        className="relative py-24 lg:py-32 border-t border-white/[0.08]"
        data-testid="forensic-section"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div
              className="lg:col-span-5 card-sharp min-h-[400px] relative overflow-hidden order-2 lg:order-1"
              style={{
                backgroundImage: `linear-gradient(180deg, rgba(5,5,5,0.5) 0%, rgba(10,13,20,1) 100%), url(${FORENSIC_IMG})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <div className="absolute top-6 left-6 tactical-label">DFIR · AR · CL</div>
              <div className="absolute bottom-6 left-6 right-6 space-y-2">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
                  Chain of Custody
                </p>
                <p className="font-mono text-2xl text-[#19A7E0]">SHA-256 · LOCKED</p>
              </div>
            </div>

            <div className="lg:col-span-7 order-1 lg:order-2">
              <p className="tactical-label">{t("landing.forensicEyebrow")}</p>
              <h2 className="mt-4 text-3xl lg:text-4xl font-medium tracking-tight">
                {t("landing.forensicTitle")}
              </h2>
              <p className="mt-5 text-base text-white/65 leading-relaxed max-w-2xl">
                {t("landing.forensicDesc")}
              </p>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: Search, key: "forensicPoint1" },
                  { icon: FileSearch, key: "forensicPoint2" },
                  { icon: Scale, key: "forensicPoint3" },
                  { icon: AlertOctagon, key: "forensicPoint4" },
                ].map(({ icon: Icon, key }) => (
                  <div
                    key={key}
                    data-testid={`forensic-point-${key}`}
                    className="card-sharp p-5 flex items-start gap-3"
                  >
                    <Icon className="w-5 h-5 text-[#19A7E0] shrink-0" />
                    <p className="text-sm text-white/85 leading-relaxed">
                      {t(`landing.${key}`)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GLOBAL FOOTPRINT */}
      <section
        id="footprint"
        className="relative py-24 lg:py-32 border-t border-white/[0.08]"
        data-testid="footprint-section"
      >
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(5,5,5,0.6) 0%, rgba(5,5,5,1) 100%), url(${GLOBAL_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
          <p className="tactical-label">{t("landing.footprintEyebrow")}</p>
          <h2 className="mt-4 text-3xl lg:text-5xl font-medium tracking-tight max-w-3xl">
            {t("landing.footprintTitle")}
          </h2>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/[0.08] border border-white/[0.08]">
            {COUNTRIES.map((c) => (
              <div
                key={c.code}
                className="bg-[#050505] p-8 lg:p-10 hover:bg-[#0A0D14] transition-colors"
                data-testid={`country-${c.code}`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-5xl font-mono font-medium text-[#19A7E0]">{c.code}</span>
                </div>
                <p className="mt-4 text-xl font-medium tracking-tight">{t(`countries.${c.name}`)}</p>
                <p className="mt-1 text-[11px] font-mono uppercase tracking-[0.2em] text-white/40">
                  {c.coords}
                </p>
                <p className="mt-6 text-sm text-white/60 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SYGNAL SHIELD — CARD SHIELD SLEEVE (Accessory line) */}
      <section
        id="sygnal-card-sleeve"
        className="relative py-24 lg:py-32 border-t border-white/[0.08]"
        data-testid="sygnal-card-section"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-stretch">
            <div className="lg:col-span-6 flex flex-col">
              <p className="tactical-label">{t("landing.sygnalCardEyebrow")}</p>
              <h2 className="mt-4 text-3xl lg:text-4xl font-medium tracking-tight">
                {t("landing.sygnalCardTitle")}
              </h2>
              <p className="mt-5 text-base text-white/65 leading-relaxed">
                {t("landing.sygnalCardDesc")}
              </p>
              <ul className="mt-8 space-y-3">
                {[
                  { icon: ShieldCheck, key: "sygnalCardFeat1" },
                  { icon: Lock, key: "sygnalCardFeat2" },
                  { icon: Scale, key: "sygnalCardFeat3" },
                ].map(({ icon: Icon, key }) => (
                  <li
                    key={key}
                    data-testid={`sygnal-card-feat-${key}`}
                    className="flex items-center gap-3 text-sm py-2 border-b border-white/[0.06]"
                  >
                    <Icon className="w-4 h-4 text-[#19A7E0]" />
                    <span>{t(`landing.${key}`)}</span>
                    <span className="ml-auto text-[10px] font-mono uppercase tracking-[0.25em] text-white/30">
                      ISO/IEC 14443
                    </span>
                  </li>
                ))}
              </ul>
              <Link
                to="/sygnal-shield"
                data-testid="sygnal-card-explore-link"
                className="mt-8 inline-flex items-center gap-2 px-6 py-3 btn-ghost text-sm font-mono uppercase tracking-[0.18em] rounded-sm self-start"
              >
                {t("landing.sygnalCardCta")} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div
              className="lg:col-span-6 card-sharp min-h-[460px] relative overflow-hidden bg-white/[0.02]"
              style={{
                backgroundImage: `linear-gradient(180deg, rgba(5,5,5,0) 50%, rgba(5,5,5,0.55) 100%), url(${SYGNAL_ACCESSORY_IMG})`,
                backgroundSize: "contain",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
              }}
              data-testid="sygnal-card-image"
            >
              <div className="absolute top-6 left-6 tactical-label">CARD SHIELD SLEEVE · ISO 14443</div>
            </div>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section
        id="trust"
        className="relative py-24 border-t border-white/[0.08]"
        data-testid="trust-section"
      >
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 text-center">
          <p className="tactical-label justify-center inline-flex" data-testid="trust-eyebrow">
            <span className="inline-block w-8 h-px bg-[#19A7E0] mr-3 align-middle self-center" />
            {t("landing.trustEyebrow")}
          </p>
          <h2 className="mt-5 text-3xl lg:text-5xl font-medium tracking-tight max-w-3xl mx-auto">
            {t("landing.trustTitle")}
          </h2>
          <p className="mt-6 text-base text-white/65 max-w-2xl mx-auto leading-relaxed">
            {t("landing.trustDesc")}
          </p>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/[0.08] border border-white/[0.08]">
            {[
              { label: "ENERGY · OIL & GAS", initials: "OG" },
              { label: "BANKING · FINANCE",  initials: "BF" },
              { label: "MINING · LATAM",     initials: "MN" },
              { label: "GOVERNMENT · DFIR",  initials: "GV" },
            ].map((s, i) => (
              <div
                key={i}
                data-testid={`trust-sector-${i}`}
                className="bg-[#050505] p-6 lg:p-8 flex flex-col items-center justify-center min-h-[140px]"
              >
                <span className="font-mono text-2xl text-[#4DA3FF] tracking-tight">{s.initials}</span>
                <p className="mt-3 text-[10px] font-mono uppercase tracking-[0.22em] text-white/55 text-center">
                  {s.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 lg:py-32 border-t border-white/[0.08]" data-testid="cta-section">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl lg:text-5xl font-medium tracking-tight max-w-3xl mx-auto">
            {t("landing.ctaTitle")}
          </h2>
          <p className="mt-6 text-base text-white/65 max-w-xl mx-auto leading-relaxed">
            {t("landing.ctaDesc")}
          </p>
          <button
            onClick={goPortal}
            data-testid="cta-button"
            className="mt-10 inline-flex items-center gap-2 px-8 py-4 btn-primary text-sm font-mono uppercase tracking-[0.18em] rounded-sm"
          >
            {t("landing.ctaPrimary")} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Landing;
